import { Box, Typography, Modal, useMediaQuery, Button, Grid, Dialog, DialogContent, TextField, CircularProgress, Hidden } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { API } from '../utils/Api';
import axios from 'axios';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Player } from '@lottiefiles/react-lottie-player';
import { AlarmOutlined, AutoAwesome, Cancel, CelebrationOutlined, CheckCircle, ChevronRightRounded } from '@mui/icons-material';
import Images from '../utils/Images';

const num = (v) => (v == null ? 0 : Number(v));
const isNum = (v) => v !== null && v !== undefined && !isNaN(Number(v));
const toId = (x) => (typeof x === 'string' ? x : x?._id);
const isFreebie = (c) => c?.isFreebieCoupon === true;

const getUnitPrice = (item) => {
  if (isNum(item?.rate)) return Number(item.rate);
  if (isNum(item?.taxable) && isNum(item?.quantity) && Number(item.quantity) > 0) {
    return Number(item.taxable) / Number(item.quantity);
  }
  const mp = item?.product?.menuProductPricing?.[0];
  if (mp?.offerPrice != null) {
    const p = typeof mp.offerPrice === 'object' ? mp.offerPrice.$numberDecimal : mp.offerPrice;
    if (isNum(p)) return Number(p);
  }
  if (isNum(item?.product?.offerPrice)) return Number(item.product.offerPrice);
  return 0;
};

const flattenUnits = (cartItems) => {
  const out = [];
  (cartItems || []).forEach((ci) => {
    const price = getUnitPrice(ci);
    const qty = Number(ci?.quantity || 0);
    const productId = toId(ci?.product) || ci?.product?._id;
    const categoryId = toId(ci?.product?.category) || ci?.product?.category;
    const mrp = isNum(ci?.mrpRate) ? Number(ci.mrpRate) : price;
    for (let i = 0; i < qty; i++) {
      out.push({
        itemId: ci._id,
        productId,
        categoryId,
        unitPrice: price,
        mrp,
      });
    }
  });
  return out;
};

const hasSizeUpgrade = (c) => !!(c?.sizeUpgradeAddon || c?.newSizeUpgradeAddon);

const excludedProductSet = (c) =>
  new Set([...(c?.excludedProducts || []), ...(c?.manuallyExcludedProducts || [])].map(toId));

const eligibleUnitsForCoupon = (coupon, allUnits) => {
  const ex = excludedProductSet(coupon);
  const incProducts = (coupon?.applicableProducts || []).map(toId);
  const incCats = (coupon?.applicableCategories || []).map(toId);

  const filterByInclusion = (u) => {
    if (incProducts.length > 0) return incProducts.includes(u.productId);
    if (incCats.length > 0) return incCats.includes(u.categoryId);
    return true;
  };

  return (allUnits || []).filter((u) => !ex.has(u.productId) && filterByInclusion(u));
};

const passesComboRequirement = (coupon, units) => {
  if (!coupon?.isComboCategoryCoupon) return true;
  const prim = new Set((coupon?.primaryComboCategories || []).map(toId));
  const sec = new Set((coupon?.secondaryComboCategories || []).map(toId));
  const hasPrim = units.some((u) => prim.has(u.categoryId));
  const hasSec = units.some((u) => sec.has(u.categoryId));
  return hasPrim && hasSec;
};

const minOrderOk = (coupon, baseSum) =>
  !isNum(coupon?.minOrderAmount) || Number(baseSum) >= Number(coupon.minOrderAmount);

const round2 = (n) => Math.round(n * 100) / 100;

const calcPercentSavings = (coupon, baseSum) => {
  if (!isNum(coupon?.discountPercent)) return 0;
  let d = (Number(coupon.discountPercent) / 100) * baseSum;
  if (isNum(coupon?.maxDiscountAmount)) d = Math.min(d, Number(coupon.maxDiscountAmount));
  d = Math.min(d, baseSum);
  return Math.max(0, round2(d));
};

const calcFlatSavings = (coupon, baseSum) => {
  if (!isNum(coupon?.discountAmount)) return 0;
  let d = Number(coupon.discountAmount);
  if (isNum(coupon?.maxDiscountAmount)) d = Math.min(d, Number(coupon.maxDiscountAmount));
  d = Math.min(d, baseSum); // never exceed base
  return Math.max(0, round2(d));
};

const calcDiscountPriceSavings = (coupon, eligUnits) => {
  if (!isNum(coupon?.discountPrice)) return 0;
  const dp = Number(coupon.discountPrice);
  if (eligUnits.length === 0) return 0;

  const qtyType = (coupon?.applicableQuantityType || 'SINGLE').toUpperCase();
  if (qtyType === 'SINGLE') {
    const unit = [...eligUnits].sort((a, b) => a.unitPrice - b.unitPrice)[0];
    const diff = unit.unitPrice - dp;
    return diff > 0 ? round2(diff) : 0;
  }

  let sum = 0;
  for (const u of eligUnits) {
    const diff = u.unitPrice - dp;
    if (diff > 0) sum += diff;
  }
  return round2(sum);
};

const calcBogoSavings = (coupon, eligUnits) => {
  const count = eligUnits.length;
  if (count < 2) return 0;
  const freeQty = Math.floor(count / 2);
  const sortedAsc = [...eligUnits].sort((a, b) => a.unitPrice - b.unitPrice);
  let sum = 0;
  for (let i = 0; i < freeQty; i++) sum += sortedAsc[i].unitPrice;
  return round2(sum);
};

const isFreebieEligible = (coupon, cartItems) => {
  // Keep the same basic eligibility gates you already use
  const units = flattenUnits(cartItems);
  const elig = eligibleUnitsForCoupon(coupon, units);

  if (!passesComboRequirement(coupon, elig)) return false;

  const baseSum = elig.reduce((s, u) => s + u.unitPrice, 0);
  if (!minOrderOk(coupon, baseSum)) return false;

  // If coupon has inclusion rules and nothing matches, it shouldn't win
  // (If you want freebie to apply even without eligible items, remove this)
  if (elig.length === 0) return false;

  return true;
};

const estimateCouponSavings = (coupon, cartItems) => {
  if (!coupon || hasSizeUpgrade(coupon)) return { coupon, savings: 0, kind: 'ignored' };

  const units = flattenUnits(cartItems);
  let elig = eligibleUnitsForCoupon(coupon, units);

  if (!passesComboRequirement(coupon, elig)) return { coupon, savings: 0, kind: 'combo-miss' };

  const baseSum = elig.reduce((s, u) => s + u.unitPrice, 0);

  if (!minOrderOk(coupon, baseSum)) return { coupon, savings: 0, kind: 'min-order' };

  let savings = 0;
  let kind = 'none';

  if (coupon?.isBogoCoupon) {
    savings = calcBogoSavings(coupon, elig);
    kind = 'bogo';
  } else if (isNum(coupon?.discountPrice)) {
    savings = calcDiscountPriceSavings(coupon, elig);
    kind = 'discountPrice';
  } else if (isNum(coupon?.discountPercent)) {
    savings = calcPercentSavings(coupon, baseSum);
    kind = 'percent';
  } else if (isNum(coupon?.discountAmount)) {
    savings = calcFlatSavings(coupon, baseSum);
    kind = 'amount';
  }

  savings = Math.max(0, Math.min(savings, baseSum));

  return { coupon, savings, kind, baseSum };
};

const isJourney = (c) => c?.isJourneyCoupon === true;

const toTime = (d) => {
  const t = new Date(d || 0).getTime();
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
};

const pickBestCoupon = (cartItems, buckets) => {
  const all = [
    ...(buckets?.birthdayCoupons || []),
    ...(buckets?.joiningCoupons || []),
    ...(buckets?.referralCoupon || []),
    ...(buckets?.applicableCoupons || []),
    ...(buckets?.validCoupons || []),
  ];

  // ---------- 1) Journey coupon FIRST ----------
  // Only consider journey coupons that are actually eligible (savings > 0 using your existing rules)
  const journeyEligible = all
    .filter(isJourney)
    .map((c) => {
      const r = estimateCouponSavings(c, cartItems);
      return { ...r, endAt: toTime(c?.validityEndDate) };
    })
    .filter((r) => (r?.savings || 0) > 0);

  if (journeyEligible.length > 0) {
    // Pick the one expiring earliest, tie-breaker = higher savings
    journeyEligible.sort((a, b) => a.endAt - b.endAt || b.savings - a.savings);
    return journeyEligible[0];
  }

  // ---------- 2) If NO journey, then Freebie ----------
  const freebie = all.find((c) => isFreebie(c) && isFreebieEligible(c, cartItems));
  if (freebie) return { coupon: freebie, savings: 0, kind: "freebie" };

  // ---------- 3) Then best savings among remaining ----------
  const scored = all
    .map((c) => estimateCouponSavings(c, cartItems))
    .filter((r) => (r?.savings || 0) > 0)
    .sort((a, b) => b.savings - a.savings);

  return scored[0] || null;
};

const CouponsComponent = ({
  cartAmount,
  cartData,
  couponData,
  refreshTrigger,
  refreshCartData,
  totalCouponDiscount,
  totalSizeUpgradeAmount
}) => {
  const [couponModal, setCouponModal] = useState(false);
  const [joiningCoupons, setJoiningCoupons] = useState([]);
  const [referralCoupon, setReferralCoupon] = useState([]);
  const [validCoupons, setValidCoupons] = useState([]);
  const [birthdayCoupons, setBirthdayCoupons] = useState([]);
  const [applicableCoupons, setApplicableCoupons] = useState([]);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [playConfetti, setPlayConfetti] = useState(false);
  const [outletData] = useState(JSON.parse(localStorage.getItem('selectedStoreData')));
  const [couponSavedModal, setCouponSaved] = useState(false);
  const [fetchingCoupons, setFetchingCoupons] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [errorText, setErrorText] = useState('');
  const [applyingBtn, setApplyingBtn] = useState({});
  const [removingCoupon, setRemovingCoupon] = useState(false);
  const [applyingCouponRefresh, setApplyingCouponRefresh] = useState(false);
  const [visibleTermsId, setVisibleTermsId] = useState(null);
  const modalRef = useRef(null);

  const handleInputChange = (newValue) => setCouponCode(newValue);

  const handleOpenCouponModal = () => {
    setCouponModal(true);
    fetchCoupons();
  };

  const handleCloseCouponModal = () => {
    setCouponModal(false);
    setCouponCode('');
    setErrorText('');
  };

  const navigateToCouponScreen = () => {
    navigate('/couponsScreen', { state: { cartAmount } });
  };

  const applyCoupon = async (couponId) => {
    if (!couponId) return;
    if (modalRef.current) modalRef.current.scrollTop = 0;
    setApplyingBtn((p) => ({ ...p, [couponId]: true }));
    setApplyingCouponRefresh(true);
    try {
      await axios.post(
        `${API.BASE_URL_2}coupons/applyCoupon/new`,
        { couponId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlayConfetti(true);
      setCouponModal(false);
      toast.success('Coupon applied successfully!!!');
      refreshCartData();
      setCouponSaved(true);
    } catch (error) {
      console.error('Error applying coupon:', error);
      toast.error(error?.response?.data?.data?.title || 'Failed to apply coupon');
    } finally {
      setApplyingBtn((p) => ({ ...p, [couponId]: false }));
      setApplyingCouponRefresh(false);
    }
  };

  const removeCoupon = async (couponId) => {
    setRemovingCoupon(true);
    try {
      await axios.post(
        `${API.BASE_URL_2}coupons/removeCoupon/new`,
        { couponId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Coupon removed successfully!!!');
      refreshCartData();
    } catch (error) {
      console.error('Error removing coupon:', error);
      toast.error(error?.response?.data?.error || 'Failed to remove coupon');
    } finally {
      setRemovingCoupon(false);
    }
  };

  const fetchCoupons = async () => {
    setFetchingCoupons(true);
    try {
      const response = await axios.get(
        `${API.BASE_URL_2}coupons/getUserApplicableCoupons/new/${outletData._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { birthdayCoupon, joiningCoupon, validCoupons, applicableCoupons, referralCoupon } =
        response.data.data.totalCoupons;
      setBirthdayCoupons(birthdayCoupon || []);
      setJoiningCoupons(joiningCoupon || []);
      setReferralCoupon(referralCoupon || []);
      setValidCoupons(validCoupons || []);
      setApplicableCoupons(applicableCoupons || []);
    } catch (error) {
      console.error('Error fetching coupons:', error);
    } finally {
      setFetchingCoupons(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  const applyCouponBtn = async () => {
    setErrorText('');
    try {
      await axios.post(
        `${API.BASE_URL_2}coupons/applyCoupon/new`,
        { couponCode: couponCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlayConfetti(true);
      setCouponModal(false);
      toast.success('Coupon applied successfully!!!');
      setCouponSaved(true);
      refreshCartData();
    } catch (error) {
      console.error('Error applying coupon:', error);
      setErrorText(error?.response?.data?.error || error?.response?.data?.data?.title || 'Invalid code');
    }
  };

  const toggleTermsVisibility = (id) => {
    setVisibleTermsId((prevId) => (prevId === id ? null : id));
  };

  const bestPick = useMemo(() => {
    console.log(cartData);

    const items =
      cartData?.cartItems ??
      cartData?.data?.cartItems ??
      cartData?.data?.data?.cartItems ??
      [];

    const buckets = {
      birthdayCoupons,
      joiningCoupons,
      referralCoupon,
      applicableCoupons,
      validCoupons,
    };

    return pickBestCoupon(items, buckets);
  }, [birthdayCoupons, joiningCoupons, referralCoupon, applicableCoupons, validCoupons, cartData?.cartItems]);

  const bestCoupon = bestPick?.coupon;
  const bestSavings = bestPick?.savings || 0;
  const isFreebieCoupon = couponData?.isFreebieCoupon === true;

  console.log('Best coupon', bestCoupon);

  const money = (n) => `₹${Number(n).toLocaleString('en-IN')}`;
  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '';

  const pickAudience = (c) => {
    const prods = (c?.applicableProducts || []).map((x) => x?.name).filter(Boolean);
    const cats = (c?.applicableCategories || []).map((x) => x?.name).filter(Boolean);
    const names = prods.length ? prods : cats;
    if (!names.length) return 'all Xpanse items';
    if (names.length === 1) return names[0];
    return `${names[0]} & ${names[1]}${names.length > 2 ? ` +${names.length - 2} more` : ''}`;
  };

  const simpleCouponSubtitle = (c) => {
    if (!c) return 'Save more with our coupons';

    const audience = pickAudience(c);
    const minOrder = c?.minOrderAmount ? ` on orders ${money(c.minOrderAmount)}+` : '';
    const ends = c?.validityEndDate
      ? (new Date(c.validityEndDate).toDateString() === new Date().toDateString()
        ? ' • Ends today'
        : ` • Ends ${fmtDate(c.validityEndDate)}`)
      : '';

    if (c?.isBogoCoupon) return `Buy 1 Get 1 Free on ${audience}${ends}`;
    if (c?.sizeUpgradeAddon) return `Free size upgrade on ${audience}${ends}`;
    if (c?.discountPrice != null) return `Only ${money(c.discountPrice)} on ${audience}${minOrder}${ends}`;
    if (c?.discountPercent != null) return `${c.discountPercent}% off on ${audience}${minOrder}${c?.maxDiscountAmount ? ` (up to ${money(c.maxDiscountAmount)})` : ''}${ends}`;
    if (c?.discountAmount != null) return `Flat ${money(c.discountAmount)} off on ${audience}${minOrder}${ends}`;

    return `Special offer on ${audience}${minOrder}${ends}`;
  };

  const FreebieSuggestedCouponCard = ({ coupon, applyCoupon }) => {
    return (
      <Box
        className="couponImageFreebie"
        sx={{
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        {/* FREEBIE IMAGE – FIXED POSITION */}
        <Box
          sx={{
            position: 'absolute',
            left: 18,        // adjust until perfectly aligned
            top: '50%',
            transform: 'translateY(-50%)',
            width: 80,
            height: 82,
            borderRadius: '14px',
            overflow: 'hidden',
            backgroundColor: '#fff',
            zIndex: 2,
          }}
        >
          <img
            src={coupon?.freebieProduct?.coverImageUrl}
            alt="freebie"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        </Box>

        {/* TEXT CONTENT */}
        <Box sx={{ flex: 1, minWidth: 0, paddingLeft: '110px' }}>
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: '15px',
              lineHeight: 1,
              color: '#0A8F4E',
            }}
          >
            FREE SNACK COUPON!
          </Typography>

          <Box
            sx={{
              my: 1.25,
              borderTop: '2px dashed #BDBDBD',
              width: '80%',
            }}
          />

          <Typography
            sx={{
              fontSize: '11px',
              color: '#7A7A7A',
              lineHeight: 1.2,
              width: '80%',
            }}
          >
            Use code <b>{bestCoupon?.couponCode}</b> and get{' '}
            {bestCoupon?.discountAmount ? (
              <b>₹{bestCoupon.discountAmount} OFF</b>
            ) : (
              simpleCouponSubtitle(bestCoupon)
            )}
          </Typography>
        </Box>
      </Box>

    );
  };

  return (
    <>
      {playConfetti && (
        <Player
          autoplay
          loop={false}
          src="https://lottie.host/aec009ac-9eae-49c8-a521-5462a46f787b/dhJlEWsRLA.json"
          className="confetti"
          style={{ position: 'fixed' }}
        />
      )}

      <Box className="couponBox">
        <Typography className="title">Smart Savings</Typography>

        <Hidden mdDown>
          {!couponData ? (
            <>
              <Box
                className="newCouponAdd"
                sx={{ cursor: 'pointer', height: 51 }}
                onClick={isMdUp ? handleOpenCouponModal : () => navigateToCouponScreen()}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                  <img className="percentImg" src={Images.couponDDiscount} />
                  <Typography className="code" sx={{ mx: 1 }}>
                    {bestCoupon ? (
                      <>
                        Save <b>₹{bestSavings.toFixed(0)}</b> with <b>{bestCoupon.couponCode}</b>
                      </>
                    ) : (
                      'Best coupon here'
                    )}
                  </Typography>
                </Box>

                <Box
                  className="applyCodeBtn"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    alignSelf: 'center',
                    height: 27,
                    opacity: 1,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (bestCoupon?._id) applyCoupon(bestCoupon._id);
                  }}
                >
                  <Typography className="applyCodeText">{bestCoupon ? 'APPLY' : 'View'}</Typography>
                </Box>
              </Box>
              <Typography
                onClick={isMdUp ? handleOpenCouponModal : () => navigateToCouponScreen()}
                sx={{ fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', marginTop: '-4px', paddingLeft: '10px', color: '#5B5B5B', alignSelf: 'right', justifySelf: 'end' }}
              >
                View all coupons <ChevronRightRounded sx={{ fontWeight: '800' }} />
              </Typography>
            </>
          ) : (
            <>
              {removingCoupon ? (
                <Box
                  className="couponNew"
                  sx={{
                    pointerEvents: 'none',
                    border: '1px dashed #B7E3D1',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress size={16} thickness={5} sx={{ mr: 1, color: '#129F67' }} />
                    <Typography className="couponNewText">Removing coupon…</Typography>
                  </Box>
                </Box>
              ) : (
                <>
                  <Box className="couponNew">
                    <Box sx={{ display: 'flex' }}>
                      {/* <CheckCircle sx={{ color: '#129F67', p: '0px 4px 0px 0px' }} />
                      <Typography className="couponNewText">
                        You saved <b>₹{totalCouponDiscount?.toFixed(2)}</b> with <b>{couponData?.couponCode}</b>
                      </Typography> */}
                      {couponData ? (
                        couponData.isFreebieCoupon ? (
                          <>
                            <CheckCircle sx={{ color: '#129F67', p: '0px 4px 0px 0px' }} />
                            <Typography className="couponNewText">
                              FREE SNACK COUPON APPLIED!
                            </Typography>
                          </>
                        ) : (
                          <>
                            <CheckCircle sx={{ color: '#129F67', p: '0px 4px 0px 0px' }} />
                            <Typography className="couponNewText">
                              You saved <b>₹{totalCouponDiscount?.toFixed(2)}</b> with <b>{couponData?.couponCode}</b>
                            </Typography>
                          </>
                        )
                      ) : null}
                    </Box>
                    <Cancel className="removeCoupon" sx={{ cursor: 'pointer' }} onClick={() => removeCoupon(couponData._id)} />
                  </Box>
                  <Typography
                    onClick={isMdUp ? handleOpenCouponModal : () => navigateToCouponScreen()}
                    sx={{ fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', paddingLeft: '20px', color: '#5B5B5B', alignSelf: 'center' }}
                  >
                    View all coupons <ChevronRightRounded sx={{ fontWeight: '800' }} />
                  </Typography>
                </>
              )}
            </>
          )}
        </Hidden>

        <Hidden mdUp>
          {/* {!couponData && (
            <Box
              className="newCouponAdd"
              sx={{ cursor: 'pointer', height: 45 }}
              onClick={isMdUp ? handleOpenCouponModal : () => navigateToCouponScreen()}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                <img className="percentImg" src={Images.couponDDiscount} />
                <Typography className="code" sx={{ mx: 0 }}>
                  Save more with our coupons
                </Typography>
              </Box>
              <Typography
                onClick={isMdUp ? handleOpenCouponModal : () => navigateToCouponScreen()}
                sx={{ fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', paddingLeft: '20px', color: '#5B5B5B', alignSelf: 'center' }}
              >
                View all coupons <ChevronRightRounded sx={{ fontWeight: '800' }} />
              </Typography>
            </Box>
          )} */}
          
          {!couponData ? (
            bestCoupon?.isFreebieCoupon ? (
              <Box>
                <Typography variant='body1' sx={{ fontWeight: 'medium', textAlign: 'center', display: 'flex', alignItems: 'center', justifySelf: 'center', color: '#1DA1F2', pb: 0, mt: 0 }}>Xpanse AI <AutoAwesome sx={{ mx: 0.5 }} /> Suggested Coupon</Typography>
                <Box sx={{ background: 'linear-gradient(180deg, #EBFF52 0%, #FEFFD9 100%)', px: 2, py: 3, borderRadius: 3, m: 0, position: 'relative' }}>
                  <FreebieSuggestedCouponCard
                    coupon={bestCoupon}
                    applyCoupon={applyCoupon}
                  />
                  <Box
                    className="applyCodeBtn"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      alignSelf: 'center',
                      height: 35,
                      opacity: 1,
                      mx: 2,
                      mt: 2,
                      mb: -1
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (bestCoupon?._id) applyCoupon(bestCoupon._id);
                    }}
                  >
                    <Typography className="applyCodeText">Apply Coupon</Typography>
                  </Box>
                </Box>
              </Box>
            ) : (
              <>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: "medium",
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    justifySelf: "center",
                    color: "#1DA1F2",
                    pb: 1,
                  }}
                >
                  Xpanse AI <AutoAwesome sx={{ mx: 0.5 }} /> Suggested Coupon
                </Typography>

                <Box
                  sx={{
                    backgroundColor: "#DFEEF5",
                    px: { xs: 1.25, sm: 2 },
                    py: { xs: 0.75, sm: 1 },
                    borderRadius: 3,
                    m: { xs: 1, sm: 1.5 },
                    position: "relative",
                  }}
                >
                  <Box className="couponImage">
                    <Box className="couponOverlay">
                      <Typography className="couponTitle">
                        SAVE{" "}
                        <span style={{ color: "#009C64" }}>
                          Rs.{Number(bestSavings || 0).toFixed(0)}
                        </span>{" "}
                        WITH{" "}
                        <span style={{ color: "#009C64" }}>
                          {bestCoupon?.couponCode}
                        </span>
                      </Typography>

                      <Typography className="couponSubtitle">
                        {simpleCouponSubtitle(bestCoupon)}
                      </Typography>
                    </Box>

                    <Box className="couponRibbon btn-anim">
                      <Typography className="couponRibbonText">
                        {bestPick?.baseSum
                          ? `${((bestSavings / bestPick.baseSum) * 100).toFixed(0)}% OFF`
                          : "BEST DEAL"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    className="applyCodeBtn"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      alignSelf: "center",
                      height: { xs: 32, sm: 35 },
                      mx: { xs: 1.25, sm: 2 },
                      my: { xs: 0.65, sm: 1 },
                      cursor: "pointer",
                      userSelect: "none",
                      touchAction: "manipulation",
                      minWidth: { xs: 150, sm: 180 },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (bestCoupon?._id) applyCoupon(bestCoupon._id);
                    }}
                  >
                    <Typography className="applyCodeText">Apply Coupon</Typography>
                  </Box>
                </Box>
              </>
            )
          ) : (
            <>
              {removingCoupon ? (
                <Box
                  className="couponNew"
                  sx={{ pointerEvents: "none", border: "1px dashed #B7E3D1" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CircularProgress
                      size={16}
                      thickness={5}
                      sx={{ mr: 1, color: "#129F67" }}
                    />
                    <Typography className="couponNewText">Removing coupon…</Typography>
                  </Box>
                </Box>
              ) : (
                <>
                  <Box className="couponNew">
                    <Box sx={{ display: "flex" }}>
                      {couponData?.isFreebieCoupon ? (
                        <>
                          <CheckCircle sx={{ color: "#129F67", p: "0px 4px 0px 0px" }} />
                          <Typography className="couponNewText">
                            FREE SNACK COUPON APPLIED!
                          </Typography>
                        </>
                      ) : (
                        <>
                          <CheckCircle sx={{ color: "#129F67", p: "0px 4px 0px 0px" }} />
                          <Typography className="couponNewText">
                            You saved <b>₹{Number(totalCouponDiscount || 0).toFixed(2)}</b>{" "}
                            with <b>{couponData?.couponCode}</b>
                          </Typography>
                        </>
                      )}
                    </Box>

                    <Cancel
                      className="removeCoupon"
                      sx={{ cursor: "pointer" }}
                      onClick={() => couponData?._id && removeCoupon(couponData._id)}
                    />
                  </Box>

                  <Typography
                    onClick={isMdUp ? handleOpenCouponModal : () => navigateToCouponScreen()}
                    sx={{
                      fontSize: "11px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      paddingLeft: "20px",
                      color: "#5B5B5B",
                      alignSelf: "center",
                    }}
                  >
                    View all coupons <ChevronRightRounded sx={{ fontWeight: "800" }} />
                  </Typography>
                </>
              )}
            </>
          )}

          {!couponData && (
            <Box
              className="newCouponAdd"
              sx={{ cursor: 'pointer', height: 45, mt: 1 }}
              onClick={isMdUp ? handleOpenCouponModal : () => navigateToCouponScreen()}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                <img className="percentImg" src={Images.couponDDiscount} />
                <Typography className="code" sx={{ mx: 0 }}>
                  View other coupons
                </Typography>
              </Box>
              <Typography
                onClick={isMdUp ? handleOpenCouponModal : () => navigateToCouponScreen()}
                sx={{ fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', paddingLeft: '20px', color: '#5B5B5B', alignSelf: 'center' }}
              >
                <ChevronRightRounded sx={{ fontWeight: '800' }} />
              </Typography>
            </Box>
          )}
        </Hidden>

      </Box>

      {isMdUp ? (
        <Modal open={couponModal} onClose={handleCloseCouponModal}>
          <Box ref={modalRef} className="couponModal" sx={{ outline: 'none' }}>
            <Box className="couponsScreen">
              {applyingCouponRefresh ? (
                <Box sx={{ height: '100%', marginTop: '30%', textAlign: 'center', alignItems: 'center', display: 'flex', flexDirection: 'column' }}>
                  <Player autoplay loop keepLastFrame src={Images.applyingCouponAnimation} style={{ height: '250px' }} />
                  <Typography className="bestTitle">Applying Your Coupon</Typography>
                </Box>
              ) : (
                <Box className="couponContainer">

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      placeholder="Enter Coupon Code"
                      value={couponCode}
                      onChange={(e) => handleInputChange(e.target.value.toUpperCase())}
                      sx={{
                        backgroundColor: '#fff',
                        '& fieldset': {
                          borderTopRightRadius: 0,
                          borderBottomRightRadius: 0,
                        },
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': { borderColor: '#E0E0E0' },
                          '&.Mui-focused fieldset': { borderColor: '#E0E0E0' },
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={applyCouponBtn}
                      sx={{
                        backgroundColor: '#1E9CED',
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                        height: '56px',
                        width: '120px',
                        '&:hover': { backgroundColor: '#1E9CED' },
                      }}
                    >
                      Apply
                    </Button>
                  </Box>
                  {errorText && (
                    <Typography variant="caption" sx={{ color: 'red', fontWeight: '500' }}>
                      {errorText}
                    </Typography>
                  )}

                  {bestCoupon && (
                    <Box className="couponBox">
                      <Typography variant='body1' sx={{ fontWeight: 'medium', textAlign: 'center', display: 'flex', alignItems: 'center', justifySelf: 'center', color: '#1DA1F2', pb: 0, mt: -4 }}>Xpanse AI <AutoAwesome sx={{ mx: 0.5 }} /> Suggested Coupon</Typography>
                      <Box sx={{ background: 'linear-gradient(180deg, #EBFF52 0%, #FEFFD9 100%)', px: 2, py: 3, borderRadius: 3, m: 0, position: 'relative' }}>

                        {/* <Box className="couponImage">
                          <Box className="couponOverlay">
                            <Typography className="couponTitle">SAVE <span style={{ color: '#009C64' }}>Rs.{bestSavings.toFixed(0)}</span> WITH <span style={{ color: '#009C64' }}>{bestCoupon?.couponCode}</span></Typography>
                            <Typography className="couponSubtitle">
                              {simpleCouponSubtitle(bestCoupon)}
                            </Typography>
                          </Box>
                          <Box className="couponRibbon btn-anim">
                            <Typography className="couponRibbonText">{bestPick?.baseSum ? `${((bestSavings / bestPick.baseSum) * 100).toFixed(0)}% OFF` : 'BEST DEAL'}</Typography>
                          </Box>
                        </Box> */}

                        {bestCoupon && bestCoupon.isFreebieCoupon ? (
                          <FreebieSuggestedCouponCard
                            coupon={bestCoupon}
                            applyCoupon={applyCoupon}
                          />
                        ) : bestCoupon ? (
                          <Box className="couponImage">
                            <Box className="couponOverlay">
                              <Typography className="couponTitle">SAVE <span style={{ color: '#009C64' }}>Rs.{bestSavings.toFixed(0)}</span> WITH <span style={{ color: '#009C64' }}>{bestCoupon?.couponCode}</span></Typography>
                              <Typography className="couponSubtitle">
                                {simpleCouponSubtitle(bestCoupon)}
                              </Typography>
                            </Box>
                            <Box className="couponRibbon btn-anim">
                              <Typography className="couponRibbonText">{bestPick?.baseSum ? `${((bestSavings / bestPick.baseSum) * 100).toFixed(0)}% OFF` : 'BEST DEAL'}</Typography>
                            </Box>
                          </Box>
                        ) : null}

                        <Box
                          className="applyCodeBtn"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            alignSelf: 'center',
                            height: 35,
                            opacity: 1,
                            mx: 2,
                            mt: 2,
                            mb: -1
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (bestCoupon?._id) applyCoupon(bestCoupon._id);
                          }}
                        >
                          <Typography className="applyCodeText">Apply Coupon</Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {fetchingCoupons ? (
                    <Box sx={{ height: 'auto', display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', marginTop: '25px' }}>
                      <Player autoplay loop keepLastFrame src={Images.applyingCouponAnimation} style={{ height: '250px' }} />
                      <Typography variant="h6" className="bestTitle" sx={{ marginTop: '10px' }}>
                        Fetching Coupons...
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {/* Birthday */}
                      {birthdayCoupons?.length > 0 && (
                        <>
                          <Typography className="bestTitle">
                            Here's your birthday gift from us <CelebrationOutlined />
                          </Typography>
                          {birthdayCoupons?.map((coupon) => (
                            <Box key={coupon._id} className="coupon birthdayCoupon">
                              <Grid container>
                                <Grid item xs={10} className="couponDetailSection">
                                  {coupon?.applicableCategories?.length > 0 && (
                                    <Typography className="couponApplyTo">
                                      Applicable to categories: {coupon?.applicableCategories?.map((c) => c?.name).join(', ')}
                                    </Typography>
                                  )}
                                  {coupon?.applicableProducts?.length > 0 && (
                                    <Typography className="couponApplyTo">
                                      Applicable to: {coupon?.applicableProducts?.map((p) => p?.name).join(', ')}
                                    </Typography>
                                  )}
                                  <div className="couponDetail">
                                    <Typography className="couponName">{coupon.couponCode}</Typography>
                                    {coupon?.discountPercent && !coupon?.discountAmount && (
                                      <Typography className="couponOff">Save {coupon?.discountPercent}% with this coupon</Typography>
                                    )}
                                    {coupon?.discountAmount && <Typography className="couponOff">Save ₹{coupon?.discountAmount} with this coupon</Typography>}
                                    {coupon?.sizeUpgradeAddon && <Typography className="couponOff">A free size upgrade on drinks!!!</Typography>}
                                    {coupon?.isBogoCoupon && <Typography className="couponOff">Buy one get one free coupon!!!</Typography>}
                                    <Typography className="couponDesc">{coupon.description}</Typography>
                                    <Box className="validAndButton" sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                                      {coupon?.validityEndDate ? (
                                        <Typography className="validity" sx={{ marginRight: 'auto' }}>
                                          Valid till: {new Date(coupon?.validityEndDate).toISOString().split('T')[0]}
                                        </Typography>
                                      ) : (
                                        <span style={{ flex: 1 }} />
                                      )}
                                      <Button className="applyButton" onClick={() => applyCoupon(coupon._id)} disabled={applyingBtn[coupon._id]}>
                                        {applyingBtn[coupon._id] ? 'Applying' : 'Apply'}
                                      </Button>
                                    </Box>
                                  </div>
                                </Grid>
                                <Grid item xs={2} className="couponDiscountSection">
                                  {coupon?.discountPercent && !coupon?.discountAmount && <Typography className="verticalFlatOff">{coupon?.discountPercent}% OFF</Typography>}
                                  {coupon?.discountAmount && <Typography className="verticalFlatOff">₹{coupon?.discountAmount} OFF</Typography>}
                                  {coupon?.sizeUpgradeAddon && <Typography className="verticalFlatOff">FREE!!!</Typography>}
                                  {coupon?.isBogoCoupon && <Typography className="verticalFlatOff">FREE!!!</Typography>}
                                  {coupon?.discountPrice && <Typography className="verticalFlatOff">{`Only ₹${coupon?.discountPrice}`}</Typography>}
                                </Grid>
                              </Grid>
                            </Box>
                          ))}
                        </>
                      )}

                      {/* Joining */}
                      {joiningCoupons?.length > 0 && (
                        <>
                          <Typography className="bestTitle">Joining Rewards</Typography>
                          {joiningCoupons?.map((coupon) => (
                            <Box key={coupon._id} className="coupon">
                              <Grid container>
                                <Grid item xs={10} className="couponDetailSection">
                                  {coupon?.applicableCategories?.length > 0 && (
                                    <Typography className="couponApplyTo">
                                      Applicable to categories: {coupon?.applicableCategories?.map((c) => c?.name).join(', ')}
                                    </Typography>
                                  )}
                                  {coupon?.applicableProducts?.length > 0 && (
                                    <Typography className="couponApplyTo">
                                      Applicable to: {coupon?.applicableProducts?.map((p) => p?.name).join(', ')}
                                    </Typography>
                                  )}
                                  <div className="couponDetail">
                                    <Typography className="couponName">{coupon.couponCode}</Typography>
                                    {coupon?.discountPercent && !coupon?.discountAmount && (
                                      <Typography className="couponOff">Save {coupon?.discountPercent}% with this coupon</Typography>
                                    )}
                                    {coupon?.discountAmount && <Typography className="couponOff">Save ₹{coupon?.discountAmount} with this coupon</Typography>}
                                    {coupon?.sizeUpgradeAddon && <Typography className="couponOff">A free size upgrade on drinks!!!</Typography>}
                                    {coupon?.isBogoCoupon && <Typography className="couponOff">Buy one get one free coupon!!!</Typography>}
                                    <Typography className="couponDesc">{coupon?.description}</Typography>
                                    <Box className="validAndButton" sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                                      {coupon?.validityEndDate ? (
                                        <Typography className="validity" sx={{ marginRight: 'auto' }}>
                                          Valid till: {new Date(coupon?.validityEndDate).toISOString().split('T')[0]}
                                        </Typography>
                                      ) : (
                                        <span style={{ flex: 1 }} />
                                      )}
                                      <Button className="applyButton" onClick={() => applyCoupon(coupon?._id)} disabled={applyingBtn[coupon?._id]}>
                                        {applyingBtn[coupon?._id] ? 'Applying' : 'Apply'}
                                      </Button>
                                    </Box>
                                  </div>
                                </Grid>
                                <Grid item xs={2} className="couponDiscountSection">
                                  {coupon?.discountPercent && !coupon?.discountAmount && <Typography className="verticalFlatOff">{coupon?.discountPercent}% OFF</Typography>}
                                  {coupon?.discountAmount && <Typography className="verticalFlatOff">₹{coupon?.discountAmount} OFF</Typography>}
                                  {coupon?.sizeUpgradeAddon && <Typography className="verticalFlatOff">FREE!!!</Typography>}
                                  {coupon?.isBogoCoupon && <Typography className="verticalFlatOff">FREE!!!</Typography>}
                                  {coupon?.discountPrice && <Typography className="verticalFlatOff">{`Only ₹${coupon?.discountPrice}`}</Typography>}
                                </Grid>
                              </Grid>
                            </Box>
                          ))}
                        </>
                      )}

                      {/* Referral */}
                      {referralCoupon?.length > 0 && (
                        <>
                          <Typography className="bestTitle">Your Referral Rewards</Typography>
                          {referralCoupon?.map((coupon) => (
                            <Box key={coupon._id} className="coupon">
                              <Grid container>
                                <Grid item xs={10} className="couponDetailSection">
                                  {coupon?.applicableCategories?.length > 0 && (
                                    <Typography className="couponApplyTo">
                                      Applicable to categories: {coupon?.applicableCategories?.map((c) => c?.name).join(', ')}
                                    </Typography>
                                  )}
                                  {coupon?.applicableProducts?.length > 0 && (
                                    <Typography className="couponApplyTo">
                                      Applicable to: {coupon?.applicableProducts?.map((p) => p?.name).join(', ')}
                                    </Typography>
                                  )}
                                  <div className="couponDetail">
                                    <Typography className="couponName">{coupon.couponCode}</Typography>
                                    {coupon?.discountPercent && !coupon?.discountAmount && (
                                      <Typography className="couponOff">Save {coupon?.discountPercent}% with this coupon</Typography>
                                    )}
                                    {coupon?.discountAmount && <Typography className="couponOff">Save ₹{coupon?.discountAmount} with this coupon</Typography>}
                                    {coupon?.sizeUpgradeAddon && <Typography className="couponOff">A free size upgrade on drinks!!!</Typography>}
                                    {coupon?.isBogoCoupon && <Typography className="couponOff">Buy one get one free coupon!!!</Typography>}
                                    <Typography className="couponDesc">{coupon.description}</Typography>
                                    <Box className="validAndButton" sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                                      {coupon?.validityEndDate ? (
                                        <Typography className="validity" sx={{ marginRight: 'auto' }}>
                                          Valid till: {new Date(coupon?.validityEndDate).toISOString().split('T')[0]}
                                        </Typography>
                                      ) : (
                                        <span style={{ flex: 1 }} />
                                      )}
                                      <Button className="applyButton" onClick={() => applyCoupon(coupon._id)} disabled={applyingBtn[coupon._id]}>
                                        {applyingBtn[coupon._id] ? 'Applying' : 'Apply'}
                                      </Button>
                                    </Box>
                                  </div>
                                </Grid>
                                <Grid item xs={2} className="couponDiscountSection">
                                  {coupon?.discountPercent && !coupon?.discountAmount && <Typography className="verticalFlatOff">{coupon?.discountPercent}% OFF</Typography>}
                                  {coupon?.discountAmount && <Typography className="verticalFlatOff">₹{coupon?.discountAmount} OFF</Typography>}
                                  {coupon?.sizeUpgradeAddon && <Typography className="verticalFlatOff">FREE!!!</Typography>}
                                  {coupon?.isBogoCoupon && <Typography className="verticalFlatOff">FREE!!!</Typography>}
                                  {coupon?.discountPrice && <Typography className="verticalFlatOff">{`Only ₹${coupon?.discountPrice}`}</Typography>}
                                </Grid>
                              </Grid>
                            </Box>
                          ))}
                        </>
                      )}

                      {/* Applicable coupons (unchanged list UI) */}
                      {applicableCoupons?.length > 0 && (
                        <>
                          <Typography className="bestTitle">Applicable Coupons</Typography>
                          {applicableCoupons?.map((coupon) => (
                            <Box key={coupon._id} className="coupon">
                              <Grid container>
                                <Grid item xs={10} className="couponDetailSection">
                                  {coupon?.applicableCategories?.length > 0 && (
                                    <Typography className="couponApplyTo">
                                      Applicable to categories:{' '}
                                      {coupon?.applicableCategories?.map((product) => product?.name).join(', ')}
                                    </Typography>
                                  )}
                                  {coupon?.applicableProducts?.length > 0 && (
                                    <Typography className="couponApplyTo">
                                      Applicable to: {coupon?.applicableProducts?.map((product) => product?.name).join(', ')}
                                    </Typography>
                                  )}
                                  <div className="couponDetail">
                                    <Typography className="couponName">{coupon.couponCode}</Typography>
                                    {coupon?.discountPercent && !coupon?.discountAmount && (
                                      <Typography className="couponOff">Save {coupon?.discountPercent}% with this coupon</Typography>
                                    )}
                                    {coupon?.discountAmount && <Typography className="couponOff">Save ₹{coupon?.discountAmount} with this coupon</Typography>}
                                    {coupon?.sizeUpgradeAddon && <Typography className="couponOff">A free size upgrade on drinks!!!</Typography>}
                                    {coupon?.isBogoCoupon && <Typography className="couponOff">Buy one get one free coupon!!!</Typography>}
                                    <Typography className="couponDesc">{coupon.description}</Typography>
                                    {coupon?.endTime && (
                                      <Typography className="couponTime">
                                        <AlarmOutlined sx={{ paddingRight: '5px' }} />{' '}
                                        {new Date(coupon?.validityEndDate).toISOString().split('T')[0] ===
                                          new Date().toISOString().split('T')[0]
                                          ? `This coupon expires today at ${coupon?.endTime}`
                                          : `Use this coupon till ${coupon?.endTime}`}
                                      </Typography>
                                    )}
                                    <Box className="validAndButton" sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                                      <Box>
                                        {coupon.validityEndDate ? (
                                          <Typography className="validity" sx={{ marginRight: 'auto' }}>
                                            Valid till: {new Date(coupon?.validityEndDate).toISOString().split('T')[0]}
                                          </Typography>
                                        ) : (
                                          <span style={{ flex: 1 }} />
                                        )}
                                        <Typography
                                          className="validity"
                                          onClick={() => toggleTermsVisibility(coupon._id)}
                                          sx={{ cursor: 'pointer', fontSize: '8px', textDecoration: 'underline', marginRight: 'auto' }}
                                        >
                                          Terms and conditions
                                        </Typography>
                                      </Box>
                                      <Button className="applyButton" onClick={() => applyCoupon(coupon._id)} disabled={applyingBtn[coupon._id]}>
                                        {applyingBtn[coupon._id] ? 'Applying' : 'Apply'}
                                      </Button>
                                    </Box>
                                  </div>
                                </Grid>
                                <Grid item xs={2} className="couponDiscountSection">
                                  {coupon?.discountPercent && !coupon?.discountAmount && <Typography className="verticalFlatOff">{coupon?.discountPercent}% OFF</Typography>}
                                  {coupon?.discountAmount && <Typography className="verticalFlatOff">₹{coupon?.discountAmount} OFF</Typography>}
                                  {coupon?.sizeUpgradeAddon && <Typography className="verticalFlatOff">FREE!!!</Typography>}
                                  {coupon?.isBogoCoupon && <Typography className="verticalFlatOff">FREE!!!</Typography>}
                                  {coupon?.discountPrice && <Typography className="verticalFlatOff">{`Only ₹${coupon?.discountPrice}`}</Typography>}
                                </Grid>
                                <Grid
                                  item
                                  xs={12}
                                  className="couponDetailSection"
                                  style={{ display: visibleTermsId === coupon._id ? 'block' : 'none' }}
                                >
                                  <ul style={{ margin: '0px' }}>
                                    {coupon?.termsAndConditions?.map((term, index) => (
                                      <li key={index}>
                                        <Typography variant="body2" className="validity">
                                          {term}{' '}
                                        </Typography>
                                      </li>
                                    ))}
                                  </ul>
                                </Grid>
                              </Grid>
                            </Box>
                          ))}
                        </>
                      )}

                      {/* Other available offers (unchanged UI) */}
                      {validCoupons?.length > 0 && (
                        <>
                          <Typography className="bestTitle">Other available offers</Typography>
                          {validCoupons?.map((coupon) => (
                            <Box key={coupon._id} className="coupon notApplicable">
                              <Grid container>
                                <Grid item xs={10} className="couponDetailSection">
                                  <div className="couponDetail">
                                    <Typography className="couponTag">{coupon?.couponMessage}</Typography>
                                    <Typography className="couponName">{coupon?.couponCode}</Typography>
                                    {coupon?.discountPercent && !coupon?.discountAmount && (
                                      <Typography className="couponOff">Save {coupon?.discountPercent}% with this coupon</Typography>
                                    )}
                                    {coupon?.discountAmount && <Typography className="couponOff">Save ₹{coupon?.discountAmount} with this coupon</Typography>}
                                    {coupon?.sizeUpgradeAddon && <Typography className="couponOff">A free size upgrade on drinks!!!</Typography>}
                                    {coupon?.isBogoCoupon && <Typography className="couponOff">Buy one get one free coupon!!!</Typography>}
                                    <Typography className="couponDesc">{coupon?.description}</Typography>
                                    <Box className="validAndButton">
                                      <Box>
                                        {coupon?.validityEndDate ? (
                                          <Typography className="validity" sx={{ marginRight: 'auto' }}>
                                            Valid till: {new Date(coupon?.validityEndDate).toISOString().split('T')[0]}
                                          </Typography>
                                        ) : (
                                          <span style={{ flex: 1 }} />
                                        )}
                                        <Typography
                                          className="validity"
                                          onClick={() => toggleTermsVisibility(coupon?._id)}
                                          sx={{ cursor: 'pointer', fontSize: '8px', textDecoration: 'underline', marginRight: 'auto' }}
                                        >
                                          Terms and conditions
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </div>
                                </Grid>
                                <Grid item xs={2} className="couponDiscountSection">
                                  {coupon?.discountPercent && !coupon?.discountAmount && <Typography className="verticalFlatOff">{coupon?.discountPercent}% OFF</Typography>}
                                  {coupon?.discountAmount && <Typography className="verticalFlatOff">₹{coupon?.discountAmount} OFF</Typography>}
                                  {coupon?.sizeUpgradeAddon && <Typography className="verticalFlatOff">FREE!!!</Typography>}
                                  {coupon?.isBogoCoupon && <Typography className="verticalFlatOff">FREE!!!</Typography>}
                                  {coupon?.discountPrice && <Typography className="verticalFlatOff">{`Only ₹${coupon?.discountPrice}`}</Typography>}
                                </Grid>
                                <Grid
                                  item
                                  xs={12}
                                  className="couponDetailSection"
                                  style={{ display: visibleTermsId === coupon._id ? 'block' : 'none' }}
                                >
                                  <ul style={{ margin: '0px' }}>
                                    {coupon?.termsAndConditions?.map((term, index) => (
                                      <li key={index}>
                                        <Typography variant="body2" className="validity">
                                          {term}{' '}
                                        </Typography>
                                      </li>
                                    ))}
                                  </ul>
                                </Grid>
                              </Grid>
                            </Box>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Modal>
      ) : null}

      <Dialog
        open={couponSavedModal}
        onClose={() => {
          setCouponSaved(false);
          setPlayConfetti(false);
        }}
        sx={{
          padding: 0,
          margin: 0,
          '& .MuiPaper-root': { borderRadius: 12, overflow: 'visible', zIndex: 1000, padding: '0px', margin: 0, justifyContent: 'center' },
        }}
      >
        {totalSizeUpgradeAmount > 0 ? (
          <img alt="Size Upgrade Image" src={Images.sizeUpModal} style={{ width: 260, height: 320 }} />
        ) : isFreebieCoupon ? (
          <img
            alt="Freebie Coupon Image"
            src={Images.freebieCoupon}
            style={{ width: 260, height: 320 }}
          />
        ) : (
          <DialogContent
            className="couponSavedModal"
            sx={{
              position: 'relative',
              bgcolor: '#fff',
              overflow: 'visible',
              justifyContent: 'center',
              borderRadius: '21px',
              padding: '0px',
              margin: '0px',
              textAlign: 'center',
              width: 260,
              height: 260,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <img className="discountImage" src={Images.couponDDiscount} style={{ maxWidth: '100%' }} />
            </div>
            <Typography sx={{ color: '#8F9190', fontSize: '15px', fontWeight: '500' }}>'{couponData?.couponCode}' applied</Typography>
            <Typography sx={{ color: '#309E77', fontSize: '22px', fontWeight: '600' }}>Woah! You're saving</Typography>
            {totalCouponDiscount > 0 && (
              <Typography sx={{ color: '#1DA1F2', fontSize: '50px', fontWeight: '600', marginTop: '-6px' }}>
                ₹{totalCouponDiscount?.toFixed(2)}
              </Typography>
            )}
            <div style={{ position: 'absolute', bottom: -4, width: '100%', textAlign: 'center' }}>
              <img src={Images.couponSavedBird} style={{ width: '70%' }} />
            </div>
            <img
              src={Images.closeButton}
              style={{ position: 'absolute', top: 8, right: 8, cursor: 'pointer' }}
              onClick={() => {
                setCouponSaved(false);
                setPlayConfetti(false);
              }}
            />
          </DialogContent>
        )}
      </Dialog>
    </>
  );
};

export default CouponsComponent;
